import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import type { ButtonHTMLAttributes } from 'react';
import { forwardRef } from 'react';

import { cn } from '@/utils';

export type ButtonVariants =
  | 'primary'
  | 'secondaryGhost'
  | 'secondaryOutline'
  | 'tertiary'
  | 'icon'
  | 'destructive'
  | 'outline'
  | 'ghost'
  | 'link';

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        // custom variants
        primary: 'bg-blue-400 font-semibold text-white hover:bg-red-500',
        secondaryGhost:
                    'border-2 border-blue-600 bg-transparent font-semibold text-blue-600 hover:bg-blue-600 hover:text-white',
        secondaryOutline:
                    'border border-white bg-transparent font-semibold text-blue-600 hover:bg-white hover:text-blue-600',
        tertiary: 'bg-blue-50 text-blue-400 hover:bg-blue-100',
        blueGhost: 'bg-transparent font-semibold text-blue-400 hover:bg-accent',
        remove: 'bg-destructive/20 font-semibold text-destructive hover:bg-destructive/30',

        // tailwind variants
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline: 'border-input border bg-white hover:bg-accent hover:text-accent-foreground',
        input: 'border-input hover:bg-accen justify-start border bg-transparent text-foreground',

        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        // custom variant
        primary: 'h-9 w-full px-4 py-2',
        input: 'h-9 px-3 py-1',
        // tailwind variants
        icon: 'size-fit p-1',
        sm: 'h-8 rounded-md px-3 text-xs',
        lg: 'h-10 rounded-md px-8 text-smm',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'primary',
    },
  },
);

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn('fill-color', buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
